import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../../../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { Box, Typography, Button, CircularProgress, IconButton, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { useCompanyLogo, useQRLogo } from '../../../hooks/useQRLogo';
import PrintCoverPage from '../../../components/PrintCoverPage';

const accent = '#E05500';

export default function WorkshopNPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState('a4');
  const [showBanner, setShowBanner] = useState(true);
  const companyLogo = useCompanyLogo();
  const qrLogo = useQRLogo();

  useEffect(() => {
    getDoc(doc(db, 'workshops', id)).then(snap => {
      if (snap.exists()) setSession({ id: snap.id, ...snap.data() });
      setLoading(false);
    });
  }, [id]);

  if (loading) return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <CircularProgress sx={{ color: accent }} />
    </Box>
  );

  if (!session) return (
    <Box sx={{ p: 4 }}>
      <Typography>Workshop hittades inte.</Typography>
    </Box>
  );

  const groups    = session.config?.groups    || [];
  const questions = session.config?.questions || [];
  const origin    = window.location.origin;

  const scanUrl = (qIdx, groupId) =>
    `${origin}/workshop-scan?s=${id}&q=${qIdx}&g=${groupId}`;

  // ── Per-storlek-config ────────────────────────────────────────────────────────
  const isA4 = pageSize === 'a4';
  const c = {
    maxWidth:           isA4 ? 750  : 1060,
    minHeight:          isA4 ? 440  : 620,
    headerPx:           isA4 ? 1.5  : 2.5,
    headerPy:           isA4 ? 0.75 : 1.25,
    headerBorder:       isA4 ? 3    : 5,
    titleSize:          isA4 ? '0.6rem'  : '0.8rem',
    dateSize:           isA4 ? '0.5rem'  : '0.62rem',
    groupLabelSize:     isA4 ? '0.44rem' : '0.54rem',
    groupNameSize:      isA4 ? '0.62rem' : '0.82rem',
    groupMinWidth:      isA4 ? 62   : 82,
    questionPt:         isA4 ? 1.25 : 2,
    questionPx:         isA4 ? 1.75 : 2.5,
    questionNumSize:    isA4 ? '0.88rem' : '1.2rem',
    questionTextSize:   isA4 ? '0.88rem' : '1.2rem',
    hintSize:           isA4 ? '0.62rem' : '0.78rem',
    scaleBoxSize:       isA4 ? 26   : 34,
    scaleBoxFontSize:   isA4 ? '0.7rem'  : '0.88rem',
    optionCircleSize:   isA4 ? 14   : 18,
    optionFontSize:     isA4 ? '0.68rem' : '0.82rem',
    tipsFontSize:       isA4 ? '0.48rem' : '0.54rem',
    answerMx:           isA4 ? 1.75 : 2.5,
    footerMx:           isA4 ? 1.75 : 2.5,
    footerLabelSize:    isA4 ? '0.46rem' : '0.54rem',
    footerTextSize:     isA4 ? '0.48rem' : '0.56rem',
    footerUrlSize:      isA4 ? '0.42rem' : '0.5rem',
    nameLabelSize:      isA4 ? '0.46rem' : '0.54rem',
    nameLineHeight:     isA4 ? 13   : 16,
    qrSize:             isA4 ? 58   : 76,
    qrLogoSize:         isA4 ? 14   : 19,
    companyLogoH:       isA4 ? 34   : 46,
  };

  return (
    <>
      {/* Screen toolbar */}
      <Box className="no-print" sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <IconButton onClick={() => navigate(`/workshopn/${id}/edit`)} sx={{ color: 'text.secondary' }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography fontWeight={700} sx={{ flex: 1 }}>
          Skriv ut hybridblad — {session.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
          {questions.length} frågor × {groups.length} grupper = {questions.length * groups.length} blad
        </Typography>
        <ToggleButtonGroup value={pageSize} exclusive size="small" onChange={(_, v) => { if (v) setPageSize(v); }} sx={{ mr: 1 }}>
          <ToggleButton value="a4" sx={{ textTransform: 'none', px: 2, fontSize: '0.78rem' }}>A4 liggande</ToggleButton>
          <ToggleButton value="a3" sx={{ textTransform: 'none', px: 2, fontSize: '0.78rem' }}>A3 liggande</ToggleButton>
        </ToggleButtonGroup>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}
          sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, textTransform: 'none', fontWeight: 600 }}>
          Skriv ut
        </Button>
      </Box>

      {/* Admin-instruktioner på skärmen */}
      {showBanner && (
        <Box className="no-print" sx={{ bgcolor: '#fff8f4', border: '1px solid #ffd5b8', borderRadius: 2, mx: 3, mt: 2, p: 2, position: 'relative' }}>
          <IconButton size="small" onClick={() => setShowBanner(false)}
            sx={{ position: 'absolute', top: 8, right: 8, color: '#aaa', '&:hover': { color: '#555' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography fontSize="0.85rem" color="#7a3a00" mb={1} pr={3}>
            <strong>Hur det fungerar:</strong> Varje blad innehåller en fråga och är kopplat till en specifik grupp via QR-koden.
            Lägg ut ett blad per bord (eller fråga). Deltagarna skriver sina svar och sina namn på bladet.
            När sessionen är klar — fotografera hela bladet (text + QR-kod syns) och låt AI tolka svaren.
          </Typography>
          <Box sx={{ bgcolor: '#fff3e0', border: '1px solid #ffcc80', borderRadius: 1, px: 2, py: 1 }}>
            <Typography fontSize="0.8rem" color="#7a3a00" fontWeight={700} mb={0.4}>
              Påminn deltagarna innan de börjar skriva:
            </Typography>
            <Typography fontSize="0.8rem" color="#8a4500" sx={{ lineHeight: 1.6 }}>
              ✏️ Skriv tydligt — gärna med VERSALER eller stor handstil<br />
              – Gör punkter för att hålla isär svar och textblock<br />
              🖊 Använd gärna en tjock penna eller tuschpenna<br />
              📄 Plats slut? Fortsätt på baksidan — baksidan behöver ingen QR-kod
            </Typography>
          </Box>
        </Box>
      )}

      {/* Utskriftsblad */}
      <Box sx={{ p: { xs: 2, md: 3 }, '@media print': { p: 0 } }}>
        {/* Försättsblad */}
        <PrintCoverPage
          type="workshop"
          title={session.title}
          subtitle={null}
          date={session.date ? new Date(session.date) : null}
          facilitator={session.facilitator || session.config?.facilitator || null}
          groups={groups}
          questions={questions.map(q => q.text)}
          companyLogo={companyLogo}
          imageUrl={session.imageUrl || session.config?.imageUrl || null}
          isA4={isA4}
        />
        {questions.map((q, qi) =>
          groups.map(g => (
            <Box
              key={`${qi}-${g.id}`}
              className="print-page"
              sx={{
                width: '100%',
                maxWidth: c.maxWidth,
                mx: 'auto',
                mb: 4,
                border: '1px solid #ddd',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: '#fff',
                color: '#000',
                display: 'flex',
                flexDirection: 'column',
                minHeight: c.minHeight,
                '@media print': {
                  border: 'none', borderRadius: 0, maxWidth: '100%', mb: 0,
                  boxShadow: 'none',
                  height: isA4 ? '194mm' : '281mm',
                  overflow: 'hidden',
                },
              }}
            >
              {/* Header */}
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 2,
                px: c.headerPx, py: c.headerPy,
                borderBottom: `${c.headerBorder}px solid ${g.color || accent}`,
                bgcolor: '#fafafa', flexShrink: 0,
              }}>
                <Box sx={{ flex: 1 }}>
                  <Typography fontSize="0.55rem" color="#888" fontWeight={700} letterSpacing={1} mb={0.2}>
                    WORKSHOP
                  </Typography>
                  <Typography fontSize={c.titleSize} fontWeight={700} color="#1a1a1a" noWrap>
                    {session.title}
                  </Typography>
                  {session.date && (
                    <Typography fontSize={c.dateSize} color="#666">
                      {new Date(session.date).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                  <Box sx={{ px: 1.5, py: 0.5, borderRadius: 1, bgcolor: (g.color || accent) + '22', border: `2px solid ${g.color || accent}`, textAlign: 'center', minWidth: c.groupMinWidth }}>
                    <Typography fontSize={c.groupLabelSize} fontWeight={700} letterSpacing={1} color={g.color || accent}>GRUPP</Typography>
                    <Typography fontSize={c.groupNameSize} fontWeight={800} color={g.color || accent}>{g.name}</Typography>
                  </Box>
                  {companyLogo && (
                    <Box
                      component="img"
                      src={companyLogo}
                      alt=""
                      sx={{ height: c.companyLogoH, maxWidth: c.companyLogoH * 3.5, objectFit: 'contain' }}
                    />
                  )}
                </Box>
              </Box>

              {/* Fråga */}
              <Box sx={{ px: c.questionPx, pt: c.questionPt, pb: 0.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Typography sx={{ color: g.color || accent, fontWeight: 800, fontSize: c.questionNumSize, lineHeight: 1, mt: 0.1, minWidth: 22 }}>
                    {qi + 1}.
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <Typography fontSize={c.questionTextSize} fontWeight={700} color="#1a1a1a" sx={{ lineHeight: 1.2 }}>
                      {q.text}
                    </Typography>
                    {q.hint && (
                      <Typography fontSize={c.hintSize} color="#555" mt={0.5} sx={{ lineHeight: 1.4 }}>
                        {q.hint}
                      </Typography>
                    )}
                    {q.type === 'skala' && (
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        {[1,2,3,4,5].map(n => (
                          <Box key={n} sx={{ width: c.scaleBoxSize, height: c.scaleBoxSize, border: `2px solid ${g.color || accent}`, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography fontWeight={700} fontSize={c.scaleBoxFontSize} color={g.color || accent}>{n}</Typography>
                          </Box>
                        ))}
                        <Typography fontSize={c.hintSize} color="#888" sx={{ alignSelf: 'center', ml: 0.5 }}>Ringa in ditt val</Typography>
                      </Box>
                    )}
                    {q.type === 'flerval' && q.options?.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        {q.options.map((opt, oi) => (
                          <Box key={oi} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                            <Box sx={{ width: c.optionCircleSize, height: c.optionCircleSize, border: `2px solid ${g.color || accent}`, borderRadius: '50%', flexShrink: 0 }} />
                            <Typography fontSize={c.optionFontSize}>{opt}</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Svarsfält med tiprad längst upp */}
              <Box sx={{
                mx: c.answerMx, mt: 0.75,
                border: `2px dashed ${g.color || accent}55`,
                borderRadius: 1.5,
                flex: 1,
                display: 'flex', flexDirection: 'column',
                '@media print': { flex: 1 },
              }}>
                {/* Tips — en enda tunn rad */}
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: isA4 ? 1.5 : 2,
                  px: 1.5, py: 0.5,
                  borderBottom: `1px dashed ${g.color || accent}28`,
                  flexShrink: 0, flexWrap: 'wrap',
                }}>
                  <Typography fontSize={c.tipsFontSize} color="#888" fontStyle="italic" noWrap>Tips:</Typography>
                  <Typography fontSize={c.tipsFontSize} color="#888" noWrap>✏ Skriv tydligt / VERSALER</Typography>
                  <Typography fontSize={c.tipsFontSize} color="#888" noWrap>– Punkter för att skilja svar åt</Typography>
                  <Typography fontSize={c.tipsFontSize} color="#888" noWrap>🖊 Tjock penna är bäst</Typography>
                  <Typography fontSize={c.tipsFontSize} color="#888" noWrap>📄 Plats slut? Fortsätt på baksidan</Typography>
                </Box>
                {/* Fri skrivyta */}
                <Box sx={{ flex: 1 }} />
              </Box>

              {/* Footer — instruktioner | deltagare | QR */}
              <Box className="print-footer" sx={{
                display: 'flex', alignItems: 'stretch',
                mx: c.footerMx, mt: 0.75, mb: 1,
                border: '1px solid #eee',
                borderRadius: 1.5,
                overflow: 'hidden',
                flexShrink: 0,
              }}>
                {/* Kolumn 1: Uppladdningsinstruktioner */}
                <Box sx={{ flex: 2, px: 1.25, py: 0.875, borderRight: '1px solid #eee' }}>
                  <Typography fontSize={c.footerLabelSize} fontWeight={700} color="#888" mb={0.35} letterSpacing={0.5}>
                    SÅ HÄR LADDAR DU UPP SVAREN
                  </Typography>
                  {[
                    '① Skanna QR-koden med mobilkamerans kamera-app',
                    '② Fotografera hela bladet (text + QR ska synas)',
                    '③ Skrivit på baksidan? Välj "Skanna baksidan" i appen',
                    '④ Granska tolkade svar och tryck Spara',
                  ].map((line, i) => (
                    <Typography key={i} fontSize={c.footerTextSize} color="#777" mb={0.15} sx={{ lineHeight: 1.3 }}>
                      {line}
                    </Typography>
                  ))}
                  <Typography fontSize={c.footerUrlSize} color="#ccc" fontFamily="monospace" mt={0.35}>
                    {scanUrl(qi, g.id).replace(origin, '')}
                  </Typography>
                </Box>

                {/* Kolumn 2: Deltagare — 5 namnrader */}
                <Box sx={{ flex: 1.5, px: 1.25, py: 0.875, borderRight: '1px solid #eee', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Typography fontSize={c.nameLabelSize} fontWeight={700} color="#888" mb={0.4} letterSpacing={0.5}>
                    DELTAGARE
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, flex: 1, justifyContent: 'space-around' }}>
                    {[0,1,2,3,4].map(n => (
                      <Box key={n} sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
                        <Typography fontSize={c.footerUrlSize} color="#ddd" sx={{ lineHeight: 1, pb: '1px', flexShrink: 0 }}>–</Typography>
                        <Box sx={{ flex: 1, borderBottom: '0.5px solid #ddd', minHeight: c.nameLineHeight }} />
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Kolumn 3: QR-kod */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: 1, py: 0.75, bgcolor: '#fff', flexShrink: 0 }}>
                  <QRCodeSVG
                    value={scanUrl(qi, g.id)}
                    size={c.qrSize}
                    level="H"
                    {...(qrLogo ? { imageSettings: { src: qrLogo, height: c.qrLogoSize, width: c.qrLogoSize, excavate: true } } : {})}
                  />
                </Box>
              </Box>
            </Box>
          ))
        )}
      </Box>

      <style>{`
        @page {
          size: ${isA4 ? 'A4' : 'A3'} landscape !important;
          margin: 8mm !important;
        }
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
          .print-page {
            page-break-after: always !important;
            break-after: page !important;
            display: flex !important;
            flex-direction: column !important;
            height: ${isA4 ? '194mm' : '281mm'} !important;
            overflow: hidden !important;
          }
          .print-page:last-child {
            page-break-after: avoid !important;
            break-after: avoid !important;
          }
          .print-footer {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }
        }
      `}</style>
    </>
  );
}
